import { Trans, useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { AlertTriangle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AllCountriesSelect } from '@/features/installations/components/all-countries-select'
import { SourceGroupSupplierInstallationSelect } from '@/features/installations/components/source-group-supplier-installation-select'
import {
  sourceGroupTypeValues,
  type SourceGroupCreateFormInput,
  type SourceGroupFormInput,
  type SourceGroupSupplierInstallationOptionDto,
  type SourceGroupType,
} from '@/features/installations/api/source-group-model'
import type { SupplierInstallationEmissionReportDto } from '@/features/suppliers/hooks/use-supplier-installation-emission-reports'

export type SourceGroupFormState = SourceGroupFormInput
export type SourceGroupCreateFormState = SourceGroupCreateFormInput

type SourceGroupFormFieldsProps = {
  form: SourceGroupFormState
  onChange: (next: SourceGroupFormState) => void
  namePrefix: string
  supplierInstallationOptions: SourceGroupSupplierInstallationOptionDto[]
  countryInvalid?: boolean
  supplierInstallationInvalid?: boolean
  emissionReportOptions?: SupplierInstallationEmissionReportDto[]
  emissionReportInvalid?: boolean
  emissionReportsLoaded?: boolean
  selectedSupplierInstallationCountry?: string | null
}

function SourceGroupTypeFields({
  value,
  onChange,
  namePrefix,
  t,
}: {
  value: SourceGroupType
  onChange: (next: SourceGroupType) => void
  namePrefix: string
  t: TFunction<'installations'>
}) {
  return (
    <fieldset className="m-0 min-w-0 space-y-3 border-0 p-0">
      <legend className="mb-2 w-full text-sm font-medium leading-none">
        {t('configuration.sourceGroups.typeField')}
      </legend>
      <div className="flex flex-col gap-3">
        {sourceGroupTypeValues.map((type) => {
          const inputId = `${namePrefix}-${type}`
          return (
            <div key={type} className="flex items-start gap-3">
              <input
                id={inputId}
                type="radio"
                name={namePrefix}
                value={type}
                checked={value === type}
                onChange={() => {
                  onChange(type)
                }}
                className="peer mt-1 size-4 shrink-0 cursor-pointer accent-primary"
              />
              <Label
                htmlFor={inputId}
                className="max-w-none cursor-pointer font-normal leading-snug peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
              >
                {t(`configuration.sourceGroups.typeLabels.${type}`)}
              </Label>
            </div>
          )
        })}
      </div>
    </fieldset>
  )
}

function UnknownSourceGroupTypeWarning() {
  const { t } = useTranslation('installations')
  const strong = <strong className="font-semibold text-foreground" />

  return (
    <Alert>
      <AlertTriangle className="size-4" />
      <AlertDescription className="space-y-3">
        <p>
          <Trans
            ns="installations"
            i18nKey="configuration.sourceGroups.unknownTypeWarning.p1"
            components={{ strong }}
          />
        </p>
        <p>
          <Trans
            ns="installations"
            i18nKey="configuration.sourceGroups.unknownTypeWarning.p2"
            components={{ strong }}
          />
        </p>
        <p>
          <Trans
            ns="installations"
            i18nKey="configuration.sourceGroups.unknownTypeWarning.p3"
            components={{ strong }}
          />
        </p>
        <p>{t('configuration.sourceGroups.unknownTypeWarning.p4')}</p>
      </AlertDescription>
    </Alert>
  )
}

function formatEmissionReportOptionLabel(
  report: SupplierInstallationEmissionReportDto
): string {
  return `${report.cnCode} · ${report.year} · ${report.productionRoute} · ${report.specificEmbeddedEmissions}`
}

function formatCountryLabel(
  countryCode: string,
  tCountry: TFunction<'countriesByIsoAlpha2'>,
  countryExists: (code: string) => boolean
): string {
  const normalizedCode = countryCode.toLowerCase()
  return countryExists(normalizedCode)
    ? tCountry(normalizedCode)
    : countryCode.toUpperCase()
}

export function SourceGroupFormFields({
  form,
  onChange,
  namePrefix,
  supplierInstallationOptions,
  countryInvalid,
  supplierInstallationInvalid,
  emissionReportOptions = [],
  emissionReportInvalid,
  emissionReportsLoaded = false,
  selectedSupplierInstallationCountry = null,
}: SourceGroupFormFieldsProps) {
  const { t } = useTranslation('installations')
  const { t: tCountry, i18n } = useTranslation('countriesByIsoAlpha2')

  const setType = (type: SourceGroupType) => {
    onChange({
      ...form,
      type,
      country: '',
      supplierInstallationId: '',
      supplierInstallationEmissionReportId: '',
    })
  }

  const showEmissionReportSelect =
    form.type === 'EXTERNAL_INSTALLATION_KNOWN' &&
    form.supplierInstallationId.trim().length > 0 &&
    emissionReportOptions.length > 0
  const showNoEmissionReportsWarning =
    form.type === 'EXTERNAL_INSTALLATION_KNOWN' &&
    form.supplierInstallationId.trim().length > 0 &&
    emissionReportsLoaded &&
    emissionReportOptions.length === 0
  const noEmissionReportsCountryLabel =
    selectedSupplierInstallationCountry?.trim().length === 2
      ? formatCountryLabel(
          selectedSupplierInstallationCountry.trim(),
          tCountry,
          (code) => i18n.exists(code, { ns: 'countriesByIsoAlpha2' })
        )
      : null

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={`${namePrefix}-description`}>
          {t('configuration.sourceGroups.descriptionField')}
        </Label>
        <Textarea
          id={`${namePrefix}-description`}
          value={form.description}
          onChange={(event) => {
            onChange({ ...form, description: event.target.value })
          }}
          rows={3}
        />
      </div>

      <SourceGroupTypeFields
        namePrefix={`${namePrefix}-type`}
        value={form.type}
        onChange={setType}
        t={t}
      />

      {form.type === 'UNKNOWN' ? (
        <div className="mt-4">
          <UnknownSourceGroupTypeWarning />
        </div>
      ) : null}

      {form.type === 'EXTERNAL_COUNTRY_KNOWN' && (
        <div className="mt-4 space-y-2">
          <Label htmlFor={`${namePrefix}-country`}>
            {t('configuration.sourceGroups.countryField')}
          </Label>
          <AllCountriesSelect
            id={`${namePrefix}-country`}
            value={form.country}
            onValueChange={(country) => {
              onChange({ ...form, country })
            }}
            placeholder={t('configuration.sourceGroups.countryPlaceholder')}
            aria-invalid={countryInvalid}
          />
        </div>
      )}

      {form.type === 'EXTERNAL_INSTALLATION_KNOWN' && (
        <div className="mt-4 space-y-2">
          <Label htmlFor={`${namePrefix}-supplier-installation`}>
            {t('configuration.sourceGroups.supplierInstallationField')}
          </Label>
          <SourceGroupSupplierInstallationSelect
            id={`${namePrefix}-supplier-installation`}
            options={supplierInstallationOptions}
            value={form.supplierInstallationId}
            onValueChange={(supplierInstallationId) => {
              onChange({
                ...form,
                supplierInstallationId,
                supplierInstallationEmissionReportId: '',
              })
            }}
            placeholder={t('configuration.sourceGroups.supplierInstallationPlaceholder')}
            aria-invalid={supplierInstallationInvalid}
          />
          {showNoEmissionReportsWarning ? (
            <Alert>
              <AlertTriangle className="size-4" />
              <AlertDescription>
                {noEmissionReportsCountryLabel
                  ? t('configuration.sourceGroups.noEmissionReportsWarningWithCountry', {
                      country: noEmissionReportsCountryLabel,
                    })
                  : t('configuration.sourceGroups.noEmissionReportsWarning')}
              </AlertDescription>
            </Alert>
          ) : null}
        </div>
      )}

      {showEmissionReportSelect ? (
        <div className="mt-4 space-y-2">
          <Label htmlFor={`${namePrefix}-emission-report`}>
            {t('configuration.sourceGroups.emissionReportField')}
            <span className="text-destructive"> *</span>
          </Label>
          <Select
            value={form.supplierInstallationEmissionReportId || undefined}
            onValueChange={(supplierInstallationEmissionReportId) => {
              onChange({
                ...form,
                supplierInstallationEmissionReportId,
              })
            }}
          >
            <SelectTrigger
              id={`${namePrefix}-emission-report`}
              aria-invalid={emissionReportInvalid}
            >
              <SelectValue
                placeholder={t('configuration.sourceGroups.emissionReportPlaceholder')}
              />
            </SelectTrigger>
            <SelectContent>
              {emissionReportOptions.map((report) => (
                <SelectItem key={report.id} value={report.id}>
                  {formatEmissionReportOptionLabel(report)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}
    </div>
  )
}

export function sourceGroupFormStateFromDto(row: {
  type: SourceGroupType
  description: string | null
  country: string | null
  supplierInstallationId: string | null
}): SourceGroupFormState {
  return {
    type: row.type,
    description: row.description ?? '',
    country: row.country ?? '',
    supplierInstallationId: row.supplierInstallationId ?? '',
    supplierInstallationEmissionReportId: '',
  }
}

export const defaultSourceGroupFormState: SourceGroupFormState = {
  type: 'INTERNAL',
  description: '',
  country: '',
  supplierInstallationId: '',
  supplierInstallationEmissionReportId: '',
}

export const defaultSourceGroupCreateFormState: SourceGroupCreateFormState =
  defaultSourceGroupFormState
